import {
  ChangeEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useDebounce } from "use-debounce";

import {
  InstoreHttpHeaderNames,
  LocalStorageKeys,
  RegistrationSortBy,
  SEARCH_INPUT_DELAY,
  TranslationContext,
} from "@is/constants";
import { ViewStateContext } from "@is/contexts";
import { useSearchStores, useSearchStoresFilters } from "@is/hooks";
import { LocalStorageService } from "@is/services";
import { api } from "@is/services/api";
import { Store } from "@is/types";
import { convertRemToPixels } from "@is/utils";

const MAX_ITEMS_PER_PAGE = 10;

const useManageSelectStore = () => {
  const { t, i18n } = useTranslation(TranslationContext.DeviceRegistration);

  const {
    selectedStore,
    searchTerm,
    country,
    sortBy,
    activeStepIndex,
    steps,
    page,
    updateViewState,
  } = useContext(ViewStateContext).registration;

  const [{ data, isPending }, searchStores] = useSearchStores();
  const { filters } = useSearchStoresFilters();

  const tableBodyRef = useRef<HTMLDivElement>(null);

  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(0);
  const [showTooltip, setShowTooltip] = useState(false);

  const [debouncedSearchTerm] = useDebounce(
    searchTerm.trim(),
    SEARCH_INPUT_DELAY,
    {
      equalityFn: (prev, next) => prev === next || next?.length === 1,
    }
  );

  useEffect(() => {
    if (data?.totalCount) {
      setTotalPages(Math.ceil(data.totalCount / itemsPerPage));
    }
  }, [data, itemsPerPage]);

  useEffect(() => {
    if (showTooltip) {
      setTimeout(() => {
        setShowTooltip(false);
      }, 2000);
    }
  }, [showTooltip]);

  useEffect(() => {
    if (tableBodyRef.current?.clientHeight) {
      const itemHeight = convertRemToPixels(1) * 6;
      const tableBodyHeight = tableBodyRef.current?.clientHeight - 70;

      const newItemsPerPage = Math.floor(tableBodyHeight / itemHeight);

      if (newItemsPerPage) {
        setItemsPerPage(
          newItemsPerPage + 1 > MAX_ITEMS_PER_PAGE
            ? MAX_ITEMS_PER_PAGE
            : newItemsPerPage + 1
        );
      }
    }
  }, []);

  useEffect(() => {
    if (itemsPerPage) {
      searchStores({
        page,
        pageSize: itemsPerPage,
        country,
        sortBy,
        searchTerm:
          debouncedSearchTerm?.length > 1 ? debouncedSearchTerm : null,
      });
    }
  }, [page, country, sortBy, debouncedSearchTerm, itemsPerPage, searchStores]);

  const countriesOptions = useMemo(() => {
    if (!filters?.countries?.length) {
      return [];
    }

    return filters.countries.map((country) => ({
      value: country.id,
      label: country.label,
    }));
  }, [filters]);

  const handleSelectStore = useCallback(
    (store: Store) => {
      const newSteps = [...steps];

      newSteps[activeStepIndex] = {
        ...newSteps[activeStepIndex],
        label: store.name,
      };

      LocalStorageService.setItem(LocalStorageKeys.StoreId, store.id);
      api.setHeader(InstoreHttpHeaderNames.StoreId, store.id);

      updateViewState({
        selectedStore: store,
        steps: newSteps,
        activeStepIndex: activeStepIndex + 1,
      });

      if (store.defaultLanguage) {
        i18n.changeLanguage(store.defaultLanguage);
      }
    },
    [activeStepIndex, i18n, steps, updateViewState]
  );

  const handleSearchChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (showTooltip) {
        setShowTooltip(false);
      }

      updateViewState({
        searchTerm: event.target.value,
        page: 1,
      });
    },
    [showTooltip, updateViewState]
  );

  const handleSearchReset = useCallback(() => {
    if (showTooltip) {
      setShowTooltip(false);
    }

    updateViewState({
      searchTerm: "",
      page: 1,
    });
  }, [showTooltip, updateViewState]);

  const handleCountryChange = useCallback(
    (options: string[]) => {
      updateViewState({
        country: options[0],
        page: 1,
      });
    },
    [updateViewState]
  );

  const handleSortByChange = useCallback(
    (options: string[]) => {
      updateViewState({
        sortBy: options[0] as RegistrationSortBy,
      });
    },
    [updateViewState]
  );

  const handlePageChange = useCallback(
    (newPage: number) => {
      updateViewState({
        page: newPage,
      });
    },
    [updateViewState]
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      const trimmedSearchTerm = searchTerm?.trim();

      if (event.key === "Enter" && trimmedSearchTerm.length <= 1) {
        setShowTooltip(true);
      }
    },
    [searchTerm]
  );

  return {
    stores: data?.items || [],
    page,
    totalPages,
    isLoading: isPending,
    selectedStore,
    searchTerm,
    country,
    countriesOptions,
    sortBy,
    tableBodyRef,
    showTooltip,
    t,
    handlePageChange,
    handleSelectStore,
    handleSearchChange,
    handleSearchReset,
    handleCountryChange,
    handleSortByChange,
    handleKeyDown,
  };
};

export default useManageSelectStore;
